import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Picture from '../components/Picture';
import AdminNav from '../components/AdminNav';
import logo from '../assets/images/logoFree.webp';
import logoFallback from '../assets/images/logoFree.png';
import '../styles/AdminLogsPage.css';
import '../styles/AdminReviewsPage.css';
import QRCode from 'qrcode';
import { clearAdminKey, getAdminKey } from '../utils/adminAuth';

const ADMIN_ENDPOINT = '/.netlify/functions/reviews-admin';
const LINK_ENDPOINT = '/.netlify/functions/reviews-link';
const INVITES_ENDPOINT = '/.netlify/functions/reviews-invites';
const DEFAULT_EXPIRES_HOURS = 24 * 7;

const formatNumber = (value) =>
    new Intl.NumberFormat('fr-FR').format(value || 0);

const formatDate = (value) => {
    if (!value) {
        return '-';
    }

    const date = new Date(value.replace(' ', 'T'));
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return new Intl.DateTimeFormat('fr-FR', {
        dateStyle: 'medium',
        timeStyle: 'short'
    }).format(date);
};

const formatRating = (value) => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
        return '-';
    }

    return `${parsed}/5`;
};

const buildHeaders = (key) => {
    const headers = {
        'Content-Type': 'application/json'
    };

    if (key) {
        headers['x-admin-key'] = key;
    }

    return headers;
};

function AdminReviewsPage() {
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');
    const [pending, setPending] = useState([]);
    const [approved, setApproved] = useState([]);
    const [invites, setInvites] = useState([]);
    const [inviteStatus, setInviteStatus] = useState('idle');
    const [qrCodes, setQrCodes] = useState({});
    const [context, setContext] = useState('');
    const [expiresHours, setExpiresHours] = useState(DEFAULT_EXPIRES_HOURS);
    const [linkData, setLinkData] = useState(null);
    const [linkStatus, setLinkStatus] = useState('idle');
    const [actionStatus, setActionStatus] = useState('idle');
    const location = useLocation();
    const navigate = useNavigate();
    const adminKey = getAdminKey();
    const redirectToLogin = useCallback(() => {
        clearAdminKey();
        navigate('/admin', {
            replace: true,
            state: { from: location }
        });
    }, [location, navigate]);

    const fetchReviews = async () => {
        setStatus('loading');
        setError('');

        try {
            const headers = buildHeaders(adminKey);
            const [pendingRes, approvedRes, invitesRes] = await Promise.all([
                fetch(`${ADMIN_ENDPOINT}?status=pending`, { headers }),
                fetch(`${ADMIN_ENDPOINT}?status=approved`, { headers }),
                fetch(INVITES_ENDPOINT, { headers })
            ]);

            if (
                pendingRes.status === 401 ||
                approvedRes.status === 401 ||
                invitesRes.status === 401
            ) {
                redirectToLogin();
                return;
            }

            if (!pendingRes.ok || !approvedRes.ok || !invitesRes.ok) {
                throw new Error('Impossible de charger les avis.');
            }

            const pendingData = await pendingRes.json();
            const approvedData = await approvedRes.json();
            const invitesData = await invitesRes.json();
            setPending(pendingData.items || []);
            setApproved(approvedData.items || []);
            setInvites(invitesData.items || []);
            setStatus('ready');
        } catch (fetchError) {
            setError(fetchError.message);
            setStatus('error');
        }
    };

    useEffect(() => {
        if (adminKey) {
            fetchReviews();
        }
        // eslint-disable-next-line
    }, [adminKey]);

    const handleGenerateLink = async () => {
        setLinkStatus('loading');
        setError('');

        try {
            const response = await fetch(LINK_ENDPOINT, {
                method: 'POST',
                headers: buildHeaders(adminKey),
                body: JSON.stringify({
                    context,
                    expiresInHours: expiresHours
                })
            });

            if (response.status === 401) {
                redirectToLogin();
                setLinkStatus('idle');
                return;
            }

            if (!response.ok) {
                throw new Error('Impossible de generer le lien.');
            }

            const payload = await response.json();
            setLinkData(payload);
            await fetchReviews();
            setLinkStatus('done');
        } catch (linkError) {
            setError(linkError.message);
            setLinkStatus('error');
        }
    };

    const handleCopyLink = async () => {
        if (!linkData?.link) {
            return;
        }

        try {
            if (!navigator?.clipboard) {
                throw new Error('Clipboard unavailable');
            }
            await navigator.clipboard.writeText(linkData.link);
            setLinkStatus('copied');
        } catch (copyError) {
            setLinkStatus('error');
        }
    };

    const handleCopyInvite = async (link) => {
        if (!link) {
            return;
        }

        try {
            if (!navigator?.clipboard) {
                throw new Error('Clipboard unavailable');
            }
            await navigator.clipboard.writeText(link);
            setInviteStatus('copied');
        } catch (copyError) {
            setInviteStatus('error');
        }
    };

    const handleAction = async (id, action) => {
        setActionStatus('loading');
        setError('');

        try {
            const response = await fetch(ADMIN_ENDPOINT, {
                method: 'POST',
                headers: buildHeaders(adminKey),
                body: JSON.stringify({ id, action })
            });

            if (response.status === 401) {
                redirectToLogin();
                setActionStatus('idle');
                return;
            }

            if (!response.ok) {
                throw new Error('Impossible de mettre a jour cet avis.');
            }

            await fetchReviews();
            setActionStatus('done');
        } catch (actionError) {
            setError(actionError.message);
            setActionStatus('error');
        }
    };

    const inviteLinks = useMemo(() => {
        if (typeof window === 'undefined') {
            return {};
        }

        const origin = window.location.origin;
        return invites.reduce((acc, invite) => {
            acc[invite.token] = `${origin}/review/${invite.token}`;
            return acc;
        }, {});
    }, [invites]);

    useEffect(() => {
        let isMounted = true;

        const buildQr = async () => {
            if (!invites.length) {
                setQrCodes({});
                return;
            }

            const next = {};
            await Promise.all(
                invites.map(async (invite) => {
                    const link = inviteLinks[invite.token];
                    if (!link) {
                        return;
                    }

                    try {
                        const dataUrl = await QRCode.toDataURL(link, {
                            width: 160,
                            margin: 1
                        });
                        next[invite.token] = dataUrl;
                    } catch (error) {
                        next[invite.token] = null;
                    }
                })
            );

            if (isMounted) {
                setQrCodes(next);
            }
        };

        buildQr();

        return () => {
            isMounted = false;
        };
    }, [invites, inviteLinks]);

    if (!adminKey) {
        return (
            <Navigate
                to='/admin'
                replace
                state={{
                    from: location
                }}
            />
        );
    }

    return (
        <div className='page admin-analytics admin-reviews'>
            <div className='admin-nav-shell'>
                <AdminNav />
            </div>
            <main>
                <section className='hero admin-hero'>
                    <div className='container admin-hero-inner'>
                        <div className='admin-hero-copy'>
                            <div className='admin-brand'>
                                <Picture
                                    webpSrc={logo}
                                    fallbackSrc={logoFallback}
                                    alt='MAF Industries'
                                    className='admin-brand-logo'
                                    imgClassName='admin-brand-logo-img'
                                />
                                <div>
                                    <span className='admin-brand-title'>
                                        MAF Industries
                                    </span>
                                    <span className='admin-brand-subtitle'>
                                        Avis clients
                                    </span>
                                </div>
                            </div>
                            <h1>Gerez les avis en attente.</h1>
                            <p className='lead'>
                                Generez un lien unique pour chaque client, puis
                                validez les avis avant publication.
                            </p>
                            {status === 'error' && (
                                <p className='admin-alert'>{error}</p>
                            )}
                            {status === 'loading' && (
                                <p className='admin-alert'>Chargement...</p>
                            )}
                        </div>
                        <div className='admin-hero-panel review-link-panel'>
                            <div className='review-link-card'>
                                <h3>Generer un lien</h3>
                                <label>
                                    Contexte (optionnel)
                                    <input
                                        type='text'
                                        value={context}
                                        onChange={(event) =>
                                            setContext(event.target.value)
                                        }
                                        placeholder='Ex : Tapis 60x60'
                                    />
                                </label>
                                <label>
                                    Expire dans
                                    <select
                                        value={expiresHours}
                                        onChange={(event) =>
                                            setExpiresHours(
                                                Number(event.target.value)
                                            )
                                        }
                                    >
                                        <option value={1}>1 heure</option>
                                        <option value={24}>1 jour</option>
                                        <option value={168}>1 semaine</option>
                                        <option value={720}>1 mois</option>
                                    </select>
                                </label>
                                <button
                                    type='button'
                                    className='button'
                                    onClick={handleGenerateLink}
                                    disabled={linkStatus === 'loading'}
                                >
                                    {linkStatus === 'loading'
                                        ? 'Generation...'
                                        : 'Generer'}
                                </button>
                                {linkData?.link && (
                                    <div className='review-link-output'>
                                        <input
                                            type='text'
                                            value={linkData.link}
                                            readOnly
                                        />
                                        <button
                                            type='button'
                                            className='button button-secondary'
                                            onClick={handleCopyLink}
                                        >
                                            {linkStatus === 'copied'
                                                ? 'Copie !'
                                                : 'Copier'}
                                        </button>
                                    </div>
                                )}
                                {linkData?.expiresAt && (
                                    <p className='review-link-note'>
                                        Expire le {formatDate(linkData.expiresAt)}
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </section>

                <section className='section'>
                    <div className='container admin-dashboard-grid'>
                        <div className='admin-card admin-card-half'>
                            <div className='admin-card-header'>
                                <div>
                                    <p className='eyebrow'>En attente</p>
                                    <h2>{formatNumber(pending.length)} avis</h2>
                                </div>
                            </div>
                            <div className='review-list'>
                                {pending.map((item) => (
                                    <div
                                        key={item.id}
                                        className='review-item'
                                    >
                                        <div className='review-meta'>
                                            <span className='review-rating'>
                                                {formatRating(item.rating)}
                                            </span>
                                            <span className='review-name'>
                                                {item.name || 'Anonyme'}
                                            </span>
                                            {item.context && (
                                                <span className='review-context-tag'>
                                                    {item.context}
                                                </span>
                                            )}
                                        </div>
                                        {item.title && (
                                            <h4>{item.title}</h4>
                                        )}
                                        <p>{item.comment}</p>
                                        <div className='review-footer'>
                                            <span>
                                                {formatDate(item.created_at)}
                                            </span>
                                            <div className='review-actions'>
                                                <button
                                                    type='button'
                                                    className='button'
                                                    onClick={() =>
                                                        handleAction(
                                                            item.id,
                                                            'approve'
                                                        )
                                                    }
                                                    disabled={
                                                        actionStatus ===
                                                        'loading'
                                                    }
                                                >
                                                    Approuver
                                                </button>
                                                <button
                                                    type='button'
                                                    className='button button-outline'
                                                    onClick={() =>
                                                        handleAction(
                                                            item.id,
                                                            'delete'
                                                        )
                                                    }
                                                    disabled={
                                                        actionStatus ===
                                                        'loading'
                                                    }
                                                >
                                                    Supprimer
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {!pending.length && (
                                    <p className='admin-alert'>
                                        Aucun avis en attente.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className='admin-card admin-card-half'>
                            <div className='admin-card-header'>
                                <div>
                                    <p className='eyebrow'>Valides</p>
                                    <h2>{formatNumber(approved.length)} avis</h2>
                                </div>
                            </div>
                            <div className='review-list'>
                                {approved.map((item) => (
                                    <div
                                        key={item.id}
                                        className='review-item'
                                    >
                                        <div className='review-meta'>
                                            <span className='review-rating'>
                                                {formatRating(item.rating)}
                                            </span>
                                            <span className='review-name'>
                                                {item.name || 'Anonyme'}
                                            </span>
                                            {item.context && (
                                                <span className='review-context-tag'>
                                                    {item.context}
                                                </span>
                                            )}
                                        </div>
                                        {item.title && (
                                            <h4>{item.title}</h4>
                                        )}
                                        <p>{item.comment}</p>
                                        <div className='review-footer'>
                                            <span>
                                                {formatDate(item.created_at)}
                                            </span>
                                            <div className='review-actions'>
                                                <button
                                                    type='button'
                                                    className='button button-outline'
                                                    onClick={() =>
                                                        handleAction(
                                                            item.id,
                                                            'delete'
                                                        )
                                                    }
                                                    disabled={
                                                        actionStatus ===
                                                        'loading'
                                                    }
                                                >
                                                    Supprimer
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {!approved.length && (
                                    <p className='admin-alert'>
                                        Aucun avis valide.
                                    </p>
                                )}
                            </div>
                        </div>

                        <div className='admin-card admin-card-full'>
                            <div className='admin-card-header'>
                                <div>
                                    <p className='eyebrow'>Invitations</p>
                                    <h2>
                                        {formatNumber(invites.length)} liens
                                        actifs
                                    </h2>
                                </div>
                            </div>
                            <div className='invite-grid'>
                                {invites.map((invite) => {
                                    const link = inviteLinks[invite.token];
                                    const qrCode = qrCodes[invite.token];

                                    return (
                                        <div
                                            key={invite.token}
                                            className='invite-item'
                                        >
                                            <div className='invite-qr'>
                                                {qrCode ? (
                                                    <img
                                                        src={qrCode}
                                                        alt={`QR ${invite.token}`}
                                                    />
                                                ) : (
                                                    <div className='invite-qr-placeholder'>
                                                        QR
                                                    </div>
                                                )}
                                            </div>
                                            <div className='invite-body'>
                                                <span className='invite-link'>
                                                    {link}
                                                </span>
                                                {invite.context && (
                                                    <span className='invite-context'>
                                                        {invite.context}
                                                    </span>
                                                )}
                                                <span className='invite-date'>
                                                    Cree le{' '}
                                                    {formatDate(
                                                        invite.created_at
                                                    )}
                                                </span>
                                                {invite.expires_at && (
                                                    <span className='invite-date'>
                                                        Expire le{' '}
                                                        {formatDate(
                                                            invite.expires_at
                                                        )}
                                                    </span>
                                                )}
                                            </div>
                                            <div className='invite-actions'>
                                                <button
                                                    type='button'
                                                    className='button button-secondary'
                                                    onClick={() =>
                                                        handleCopyInvite(link)
                                                    }
                                                >
                                                    Copier
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                                {!invites.length && (
                                    <p className='admin-alert'>
                                        Aucun lien actif.
                                    </p>
                                )}
                                {inviteStatus === 'copied' && (
                                    <p className='invite-feedback'>
                                        Lien copie !
                                    </p>
                                )}
                                {inviteStatus === 'error' && (
                                    <p className='invite-feedback'>
                                        Impossible de copier le lien.
                                    </p>
                                )}
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </div>
    );
}

export default AdminReviewsPage;
